import { ConditionalLink } from "components/conditionalLink";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { ChatNotification } from "components/icons/ChatNotification";
import { SmallRocket } from "components/icons/SmallRocket";
import { US } from "constants/countryCodes";
import { motion } from "framer-motion";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import FreeFilingBG from "static/images/FreeFilingBG.svg";
import { useCreateTaskMutation } from "store/apis/getTemplates";
import { useGetFreeFilingBannerDetailsQuery } from "store/apis/taxHome";
import { UpcomingDeadlineData } from "types/Models/taxHome";
import { BackendError } from "types/utils/error";

export const FreeFilingBanner = () => {
  const {
    entities,
    is_free_task_coupon: isFreeTaskCoupon,
    secondary_channel_url,
    uuid: groupId,
  } = useCurrentGroupContext();
  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation();
  const { promise: promiseToast } = useToast();
  const history = useHistory();
  const usEntities = entities?.filter((e) => e.country_code === US) || [];
  const entityId = usEntities[0]?.uuid;
  const {
    data: freeFilingBanner,
    isSuccess,
    isLoading,
  } = useGetFreeFilingBannerDetailsQuery(
    {
      groupId: groupId,
    },
    {
      skip: !isFreeTaskCoupon || !groupId,
      refetchOnMountOrArgChange: true,
    }
  );

  const handleFilingAdd = async () => {
    const baseTaskTemplateId = (freeFilingBanner as UpcomingDeadlineData)
      .base_task_template.uuid;
    if (freeFilingBanner && baseTaskTemplateId) {
      promiseToast(
        createTask({
          entity: entityId,
          metaData: {
            ...freeFilingBanner,
            base_task_template_id: baseTaskTemplateId,
          },
        }).unwrap(),
        {
          loading: "Your filing is being created, please wait.",
          success: (task) => {
            history.replace(`/filings/${task.uuid}`);
            return "Task launched successfully";
          },
          error: (error) => {
            return (error as BackendError)?.data?.error?.message;
          },
        }
      );
    }
  };

  if (!isFreeTaskCoupon) {
    return null;
  }

  return (
    <Async.Root
      isLoading={isLoading}
      isError={false}
      isSuccess={isSuccess}
      isEmpty={Object.keys(freeFilingBanner || {}).length === 0}
      // Async.Root treats a falsy renderOnLoad as "use the default loader",
      // which is a 580px block — this banner shows nothing until it resolves.
      renderOnLoad={<></>}
    >
      <Async.Empty>{null}</Async.Empty>
      <Async.Success>
        <>
          <motion.div
            layout
            className="t-w-full hover:t-shadow-light-30 t-transition-shadow t-rounded-lg t-overflow-hidden t-col-span-2 t-mb-4 t-border-solid t-border-[0.509px] t-border-neutral-10"
          >
            <div className="t-relative t-py-5 t-px-6">
              <img
                src={FreeFilingBG}
                alt="Free filing background"
                className="t-absolute t-top-0 t-left-0 t-h-full t-w-full t-object-cover t-z-[-1]"
              />
              <div className="t-flex t-justify-between t-items-start t-gap-4">
                <div className="t-flex t-flex-col t-gap-3">
                  <SmallRocket className="t-size-5 t-text-purple-50" />
                  <div className="t-flex t-flex-col t-gap-1">
                    <div className="t-text-subtitle t-text-text-100">
                      Get started with your free{" "}
                      {
                        (freeFilingBanner as UpcomingDeadlineData)
                          ?.deadline_name
                      }{" "}
                      filing
                    </div>
                    <div className="t-text-body-sm t-text-text-30">
                      Launch your filing at no cost. Fill in a few details and
                      our team will take care of the rest. If you have any
                      questions, feel free to reach us out.
                    </div>
                  </div>
                  <div className="t-flex t-items-center t-gap-2 t-mt-2">
                    <Button
                      size="small"
                      customType="primary"
                      isLoading={isCreating}
                      onClick={handleFilingAdd}
                      disabled={isCreating}
                    >
                      Launch filing
                    </Button>
                    <ConditionalLink to={`/chat/${secondary_channel_url}`}>
                      <Button size="small">Talk to the team</Button>
                    </ConditionalLink>
                  </div>
                </div>
                <div className="t-px-3 t-py-1 t-rounded-2xl t-bg-surface t-flex t-gap-1 t-items-center t-whitespace-nowrap">
                  <ChatNotification className="t-text-purple" />
                  <span className="t-text-body-sm t-text-purple-70">
                    Free filing
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      </Async.Success>
    </Async.Root>
  );
};
